import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type RegistrationStepperProps = {
  currentStep: number;
  isPkiLoginV2: boolean;
};

export function RegistrationStepper({ currentStep, isPkiLoginV2 }: RegistrationStepperProps) {
  const { t } = useTranslation();
  const accountStep = {
    title: t('register.steps.account.title'),
    description: t('register.steps.account.description'),
  };
  const residencyStep = {
    title: t('register.steps.residency.title'),
    description: t('register.steps.residency.description'),
  };
  const steps = [
    ...(isPkiLoginV2 ? [residencyStep, accountStep] : [accountStep, residencyStep]),
    {
      title: t('register.steps.personalDetails.title'),
      description: t('register.steps.personalDetails.description'),
    },
    {
      title: t('register.steps.verification.title'),
      description: t('register.steps.verification.description'),
    },
  ];

  return (
    <nav className="mb-8" aria-label={t('register.steps.label')}>
      <ol className="grid grid-cols-2 gap-2">
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const isComplete = stepNumber < currentStep;
          const isCurrent = stepNumber === currentStep;

          return (
            <li
              key={step.title}
              aria-current={isCurrent ? 'step' : undefined}
              className={cn(
                'rounded-lg border p-3',
                isCurrent ? 'border-brand-medium bg-states-base-bg-control' : 'border-border bg-white',
              )}
            >
              <div className="flex items-start gap-2">
                <span
                  className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-bold',
                    (isCurrent || isComplete) && 'border-brand-medium bg-brand-medium text-white',
                  )}
                  aria-hidden="true"
                >
                  {isComplete ? <Check className="h-4 w-4" /> : stepNumber}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-foreground">{step.title}</span>
                  <span className="mt-1 block text-xs leading-4 text-muted-foreground">{step.description}</span>
                </span>
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
